/**
 * ColumnSelector (docs/04 §5, ADR-23) — the control that makes compact density
 * worth having. Dense rows show more records; choosing the columns is what
 * makes them the right records to be looking at.
 *
 * The chosen set is per user and stored SERVER-side (docs/04 §5), so it follows
 * whoever set it to their laptop, the shared workstation and their phone. The
 * catalog, the locked-column rule and the resolution order all live in
 * `@propos/shared` so the API validates exactly what this renders.
 *
 * Radix supplies the popover a11y plumbing (focus management, escape,
 * click-outside, aria wiring) as docs/08 sanctions — hand-rolling that is where
 * keyboard traps come from.
 *
 * The rows are `aria-pressed` toggle buttons, not `menuitemcheckbox` in a menu.
 * They were the latter, which forced the whole panel to be a menu — and a menu
 * row may not contain sibling controls, so there was nowhere to put the reorder
 * buttons this panel now needs. Toggle buttons are what they always were
 * behaviourally (this is a set of independent switches, not a command menu),
 * they carry state in `aria-pressed` the way DensityControl does, and a row can
 * hold the two move controls beside them.
 */

import { TABLE_CATALOGS, type TableId } from '@propos/shared';
import * as RadixPopover from '@radix-ui/react-popover';
import { clsx } from 'clsx';
import { ArrowDown, ArrowUp, Check, Columns3, Lock } from 'lucide-react';
import type { ReactNode } from 'react';

export interface ColumnSelectorProps {
  readonly tableId: TableId;
  /** Currently visible ids, locked columns included. */
  readonly visible: readonly string[];
  readonly onChange: (columns: readonly string[]) => void;
  /** Restores the catalog defaults. */
  readonly onReset: () => void;
  readonly isDefault: boolean;
  readonly busy?: boolean | undefined;
  /**
   * Visible ids in the user's chosen order, and the setter for it. Omit both to
   * render the panel without reordering — right for a table whose column order
   * is structural rather than a preference.
   */
  readonly order?: readonly string[] | undefined;
  readonly onOrderChange?: ((order: readonly string[]) => void) | undefined;
}

export function ColumnSelector({
  tableId,
  visible,
  onChange,
  onReset,
  isDefault,
  busy = false,
  order,
  onOrderChange,
}: ColumnSelectorProps): ReactNode {
  const catalog = TABLE_CATALOGS[tableId];
  const shown = new Set(visible);
  const reorderable = order !== undefined && onOrderChange !== undefined;

  /**
   * Rows render in the CHOSEN order when reordering is on, with the columns
   * that are switched off after them in catalog order. Two reasons: the list
   * then reads as the table reads, and moving a column means moving the row you
   * are looking at rather than one somewhere else in a fixed list.
   */
  const rows = reorderable
    ? [
        ...order.map((id) => catalog.find((column) => column.id === id)).filter(isColumn),
        ...catalog.filter((column) => !shown.has(column.id)),
      ]
    : catalog;

  const move = (id: string, delta: -1 | 1): void => {
    if (!reorderable || busy) return;
    const next = [...order];
    const from = next.indexOf(id);
    const to = from + delta;
    if (from === -1 || to < 0 || to >= next.length) return;
    const [held] = next.splice(from, 1);
    if (held === undefined) return;
    next.splice(to, 0, held);
    onOrderChange(next);
  };
  // Locked columns are not part of the count: they cannot be turned off, so
  // including them would make "3 of 14" read as though 3 were chosen.
  const optional = catalog.filter((column) => column.locked !== true);
  const chosen = optional.filter((column) => shown.has(column.id)).length;

  const toggle = (id: string): void => {
    const next = optional
      .filter((column) => (column.id === id ? !shown.has(id) : shown.has(column.id)))
      .map((column) => column.id);
    onChange(next);
  };

  return (
    <RadixPopover.Root>
      <RadixPopover.Trigger
        className={clsx(
          'inline-flex min-h-11 items-center gap-1.5 rounded-ctl border border-card-line bg-card',
          'px-3 py-2 text-body font-medium text-ink2 hover:text-ink',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700',
        )}
        aria-label={`Choose columns — ${String(chosen)} of ${String(optional.length)} shown`}
      >
        <Columns3 size={16} aria-hidden />
        Columns
        {isDefault ? null : (
          <span className="rounded-full bg-surface-hover px-1.5 text-chip font-semibold text-ink2">
            {chosen}
          </span>
        )}
      </RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          align="end"
          sideOffset={6}
          className={clsx(
            'z-20 w-64 rounded-card border border-card-line bg-card p-2 shadow-lg',
            'max-h-[min(70vh,26rem)] overflow-y-auto',
          )}
        >
          <p className="px-2 pb-1 pt-1 text-chip font-semibold uppercase tracking-wide text-ink2">
            Columns
          </p>
          <ul aria-label="Choose columns" className="flex flex-col">
            {rows.map((column) => {
              const locked = column.locked === true;
              const on = locked || shown.has(column.id);
              const position = reorderable ? order.indexOf(column.id) : -1;
              return (
                <li key={column.id} className="flex items-center gap-0.5">
                  <button
                    type="button"
                    aria-pressed={on}
                    aria-disabled={locked || busy || undefined}
                    title={locked ? 'Always shown — it identifies the row' : undefined}
                    onClick={() => {
                      if (locked || busy) return;
                      toggle(column.id);
                    }}
                    className={clsx(
                      'flex grow min-h-(--tap-min) items-center gap-2 rounded-ctl px-2 py-1.5',
                      'text-left text-body',
                      locked
                        ? 'cursor-not-allowed text-ink2'
                        : 'text-ink hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-700',
                    )}
                  >
                    <span
                      aria-hidden
                      className={clsx(
                        'flex size-4 shrink-0 items-center justify-center rounded-[4px] border',
                        on ? 'border-brand-700 bg-brand-700 text-on-brand' : 'border-control-line',
                      )}
                    >
                      {on ? <Check size={12} strokeWidth={3} /> : null}
                    </span>
                    <span className="grow truncate">{column.label}</span>
                    {locked ? <Lock size={12} aria-hidden className="shrink-0" /> : null}
                  </button>
                  {/* Arrow BUTTONS rather than a drag handle. A drag needs a
                      keyboard equivalent to be usable at all, and once that
                      exists the drag is the redundant half — so this ships the
                      half everybody can reach. Only visible columns can move;
                      a hidden column has no position in the table. */}
                  {reorderable && position !== -1 ? (
                    <span className="flex shrink-0">
                      <MoveButton
                        label={`Move ${column.label} earlier`}
                        disabled={position === 0 || busy}
                        onClick={() => {
                          move(column.id, -1);
                        }}
                      >
                        <ArrowUp size={13} aria-hidden />
                      </MoveButton>
                      <MoveButton
                        label={`Move ${column.label} later`}
                        disabled={position === order.length - 1 || busy}
                        onClick={() => {
                          move(column.id, 1);
                        }}
                      >
                        <ArrowDown size={13} aria-hidden />
                      </MoveButton>
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
          <div className="mt-1 border-t border-hairline pt-1">
            <button
              type="button"
              onClick={() => {
                if (!isDefault && !busy) onReset();
              }}
              aria-disabled={isDefault || busy || undefined}
              title={isDefault ? 'Already showing the default columns' : undefined}
              className={clsx(
                'flex w-full min-h-(--tap-min) items-center rounded-ctl px-2 py-1.5 text-meta font-medium',
                isDefault
                  ? 'cursor-not-allowed text-ink2'
                  : 'text-brand-700 hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-700',
              )}
            >
              Reset to defaults
            </button>
          </div>
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}

/** Type guard for the catalog lookup above — an id with no column is dropped. */
function isColumn<T>(column: T | undefined): column is T {
  return column !== undefined;
}

function MoveButton({
  label,
  disabled,
  onClick,
  children,
}: {
  readonly label: string;
  readonly disabled: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      aria-label={label}
      aria-disabled={disabled || undefined}
      onClick={() => {
        if (!disabled) onClick();
      }}
      className={clsx(
        'flex size-7 items-center justify-center rounded-ctl',
        disabled
          ? 'cursor-not-allowed text-ink3'
          : 'text-ink2 hover:bg-surface-hover hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-700',
      )}
    >
      {children}
    </button>
  );
}
